import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';

// Checkbox dropdown for form fields (RON-5769 Quick Booking > Zone).
// Same trigger and panel as the other step-1 dropdowns (ctm-dropdown), with no inner label:
// the form renders its own label above the control. Ticked rows show green text.
function CustomCheckboxDropdown({
  items = [],
  value,
  labelKey,
  valueKey,
  placeholder = "Select",
  noOptionsMessage = "",
  onChange,
  searchable = true,
  disabled = false,
  multiple = false,
  showSelectAll = false,
  error = false,
  rootClassName = ""
}) {
  const { t } = useTranslation("common");

  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [touched, setTouched] = useState(false); // green border once the user changes the value
  const dropdownRef = useRef(null);

  const selectedValues = multiple ? value || [] : [];

  const selectedItem =
    !multiple && items.find((item) => item[valueKey] === value);

  const selectedItems = multiple
    ? items.filter((item) => selectedValues.includes(item[valueKey]))
    : [];

  const filteredItems = useMemo(() => {
    if (!searchable) return items;

    return items.filter((item) =>
      item[labelKey]
        ?.toString()
        .toLowerCase()
        .includes(searchText.toLowerCase())
    );
  }, [items, searchText, searchable, labelKey]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchText("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
      setSearchText("");
    }
  };

  const handleSelect = (item) => {
    setTouched(true);

    if (!multiple) {
      onChange(item[valueKey]);
      setIsOpen(false);
      setSearchText("");
      return;
    }

    const newValues = selectedValues.includes(item[valueKey])
      ? selectedValues.filter((v) => v !== item[valueKey])
      : [...selectedValues, item[valueKey]];

    onChange(newValues);
  };

  const handleSelectAll = () => {
    setTouched(true);
    onChange(selectedValues.length === items.length ? [] : items.map((i) => i[valueKey]));
  };

  const selectedLabel = () => {
    if (multiple) {
      const count = selectedItems.length;

      if (count === 0) return placeholder;
      if (count === 1) return selectedItems[0][labelKey];

      return t("react_selected").replace("%s", count);
    }

    return selectedItem ? selectedItem[labelKey] : placeholder;
  };

  const allSelected = items.length > 0 && selectedValues.length === items.length;

  const isChecked = (item) =>
    multiple ? selectedValues.includes(item[valueKey]) : value === item[valueKey];

  const renderRow = (key, checked, label, onClick) => (
    <li
      key={key}
      className={checked ? "ctm-checkbox-checked" : ""}
      onClick={onClick}
    >
      <input
        type="checkbox"
        className="form-check-input"
        checked={checked}
        readOnly
        tabIndex={-1}
      />
      <a>{label}</a>
    </li>
  );

  return (
    <div
      className={`${rootClassName} ctm-dropdown-inner ctm-checkbox-dropdown d-block position-relative`.trim()}
      ref={dropdownRef}
    >
      <a
        className={[
          "ctm-dropdown",
          isOpen ? "dropdown-open" : "",
          disabled ? "ctm-dropdown-disabled" : "",
          touched && !error ? "ctm-success" : "",
          error ? "error-form" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={toggleDropdown}
      >
        {selectedLabel()}
      </a>

      {isOpen && !disabled && (
        <div className="ctm-dropdown-panel">
          {searchable && (
            <div className="px-3 pt-3 pb-2">
              <input
                type="text"
                className="form-control"
                placeholder={t("search")}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                autoFocus
              />
            </div>
          )}

          <ul className="ctm-dropdown-menu">
            {multiple && showSelectAll &&
              renderRow("select-all", allSelected, t("select_all"), handleSelectAll)}

            {filteredItems.length > 0 ? (
              filteredItems.map((item) =>
                renderRow(item[valueKey], isChecked(item), item[labelKey], () => handleSelect(item))
              )
            ) : (
              <div style={{ padding: "8px 12px", color: "#9ca3af" }}>
                {noOptionsMessage || t("react_no_options_found")}
              </div>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default CustomCheckboxDropdown;
